/** Single source of truth for the "where do users land post-auth" path.
 *  Used by the proxy, every Server Action, and the OAuth callback. */
export const POST_AUTH_DEFAULT = "/";

/** Callback query param marking a round-trip as the one-shot OAuth
 *  auto-retry (#685 — see `oauth-retry.ts`). Presence (any value) means
 *  "already retried once — don't retry again", which is what makes the
 *  retry loop-proof. Namespaced (`oauth_`) so an unrelated `?retry=` from
 *  some future page/link can never disarm the rescue. These constants live
 *  with `buildCallbackUrl`, their sole writer (moving them into
 *  oauth-retry.ts would also import-cycle back into this module). */
export const OAUTH_RETRY_PARAM = "oauth_retry";

/** Callback query param naming the OAuth provider that started the flow —
 *  threaded through `redirectTo` by `oauthParams`. Load-bearing for
 *  the auto-retry: the /auth/callback code exchange is shared with
 *  EMAIL-LINK flows (password recovery, signup confirmation), which carry
 *  no marker, and only provider-marked flows may be restarted (#685 —
 *  see `oauth-retry.ts`). */
export const OAUTH_PROVIDER_PARAM = "provider";

/** Same-origin path validator. The URL ctor against a dummy origin
 *  catches absolute cross-origin URLs (`https://evil.com/path`),
 *  protocol-relative (`//evil`), and backslash variants (`/\evil`,
 *  which Chrome's URL parser normalizes to `//evil`). */
export function safeNext(
  raw: string | null | undefined,
  fallback: string,
): string {
  if (!raw) return fallback;
  try {
    const parsed = new URL(raw, "http://localhost");
    if (parsed.origin !== "http://localhost") return fallback;
    if (parsed.pathname.startsWith("//")) return fallback;
    return parsed.pathname + parsed.search;
  } catch {
    return fallback;
  }
}

/** Where the proxy sends a signed-in visitor to /login or /signup: their
 *  `next` when it's a safe path, so a "Sign in again" link that finds a live
 *  session still lands back on the page. Never /login or /signup itself. */
export function signedInRedirect(next: string | null | undefined): string {
  const target = safeNext(next, POST_AUTH_DEFAULT);
  const path = target.split("?")[0];
  return path === "/login" || path === "/signup" ? POST_AUTH_DEFAULT : target;
}

/** Absolute callback URL for OAuth + email-confirm flows. Threads `next`
 *  into the query string when present and not the default — the absent
 *  param is the signal "go to POST_AUTH_DEFAULT", so encoding it is
 *  pointless noise. `provider` marks the flow as a provider-OAuth start
 *  (vs an email-link exchange); `retry` appends the one-shot auto-retry
 *  marker so the callback can tell a retried round-trip from a first
 *  attempt (#685). */
export function buildCallbackUrl(
  origin: string,
  next?: string | null,
  opts?: { retry?: boolean; provider?: string },
): string {
  return withAuthReturnParams(new URL("/auth/callback", origin), next, opts);
}

/** Custom-scheme deep link the native shell registers (CFBundleURLTypes in
 *  apps/mobile). In the shell, OAuth's FINAL Supabase redirect targets this
 *  instead of the https callback: the round-trip runs in the system
 *  browser, and an https callback would complete the session in Safari's
 *  cookie jar while the app stays logged out. The scheme URL re-opens the
 *  app instead; the shell bridge (components/shell-bridge.tsx) then
 *  forwards the `?code=` into the webview's own /auth/callback, where the
 *  PKCE verifier cookie lives. Allowlisted in `supabase/config.toml`
 *  additional_redirect_urls as `medceptor://oauth-return**` — the glob is
 *  load-bearing, GoTrue matches the FULL redirect URL including the query
 *  string this builder always appends. Prod propagation is the canonical
 *  `config push` flow (see the config.toml entry). */
export const SHELL_OAUTH_RETURN_URL = "medceptor://oauth-return";

/** Shared param threading for the two OAuth return targets — one writer so
 *  the https callback and the shell deep link can't drift on vocabulary. */
function withAuthReturnParams(
  url: URL,
  next?: string | null,
  opts?: { retry?: boolean; provider?: string },
): string {
  const value = nextParamValue(next);
  if (value) url.searchParams.set("next", value);
  if (opts?.provider) url.searchParams.set(OAUTH_PROVIDER_PARAM, opts.provider);
  if (opts?.retry) url.searchParams.set(OAUTH_RETRY_PARAM, "1");
  return url.toString();
}

/** Shell counterpart of `buildCallbackUrl`: same `next`/`provider`/`retry`
 *  threading, but targeting the app's custom scheme. */
export function buildShellReturnUrl(
  next?: string | null,
  opts?: { retry?: boolean; provider?: string },
): string {
  return withAuthReturnParams(new URL(SHELL_OAUTH_RETURN_URL), next, opts);
}

/** The value a `?next=` param should carry, or null for "omit it": absent,
 *  cross-origin (re-validated defensively — a caller forgetting `safeNext`
 *  would otherwise have an open-redirect footgun), or the default landing
 *  page, which every flow lands on anyway so encoding it is noise. The one
 *  owner of that rule for the OAuth return URLs and `withNext` alike. */
function nextParamValue(next?: string | null): string | null {
  const safe = safeNext(next, "");
  return safe && safe !== POST_AUTH_DEFAULT ? safe : null;
}

/** Append `?next=` to a path when it carries information. Used for
 *  cross-flow links (login footer → signup, signup footer → login) and the
 *  gates' redirects (auth wall, onboarding) so the original-intent path
 *  survives the detour. */
export function withNext(path: string, next?: string | null): string {
  const value = nextParamValue(next);
  return value ? `${path}?next=${encodeURIComponent(value)}` : path;
}
